/**
 * Box S, the MMM scope (docs/superpowers/specs/2026-10-28-native-budget-mmm-design.md §4.2, §5.9): every ad account of
 * the workspace with its live 28-day spend, and whether it may join the scope and is in it. An account that paused
 * within the 28 days is still listed, so it can join. Pure.
 *
 * @module services/budget/scopeRows
 */
import { addNullable, ratioOf, totalsOf } from '@/services/overview/overviewMath';
import type { FactRow } from '@/services/overview/overviewTypes';

/** One media_platform_accounts row. */
export interface AdAccount {
  id: string;
  accountName: string | null;
  externalAccountId: string;
  platform: string;
  /** null = unknown: the account can never join a scope (spec §5.9). */
  currency: string | null;
}

export type ScopeRow = AdAccount & {
  /** null = no spend reported in the 28 days, or an unknown currency. */
  spend28: number | null;
  /** Of the listed accounts' spend in the same currency. */
  share: number | null;
  inScope: boolean;
  selectable: boolean;
};

export const nullLast = (a: unknown, b: unknown): number => Number(a === null) - Number(b === null);

type Ranked = Pick<AdAccount, 'id' | 'accountName'> & { spend: number | null };

/** T10g: spend, highest first, then the name, nulls last in both, then the id. */
export const accountOrder = (a: Ranked, b: Ranked): number =>
  nullLast(a.spend, b.spend) || (b.spend ?? 0) - (a.spend ?? 0)
  || nullLast(a.accountName, b.accountName) || (a.accountName ?? '').localeCompare(b.accountName ?? '')
  || a.id.localeCompare(b.id);

/** scopeIds null = nothing confirmed yet, so every account that may join is in (T10b). */
export const scopeRowsOf = (accounts: readonly AdAccount[], current: readonly FactRow[], scopeIds: readonly string[] | null): ScopeRow[] => {
  const listed = accounts
    .filter((a) => a.platform !== 'analytics')
    .map((a) => ({ ...a, spend: a.currency === null ? null : totalsOf(current.filter((r) => r.accountId === a.id)).spend }));
  const totalIn = (currency: string): number | null =>
    listed.filter((a) => a.currency === currency).reduce<number | null>((s, a) => addNullable(s, a.spend), null);
  return [...listed].sort(accountOrder).map(({ spend, ...a }) => ({
    ...a,
    spend28: spend,
    share: a.currency === null ? null : ratioOf(spend, totalIn(a.currency)),
    inScope: a.currency !== null && (scopeIds === null || scopeIds.includes(a.id)),
    selectable: a.currency !== null,
  }));
};
